/**
 * Much of this is extremely similar to the Header component's scripts.
 * If making changes, consider making them in both places.
 */

/**
 * Animation duration
 */
const DURATION: KeyframeEffectOptions["duration"] = 300;

/**
 * Animation easing function
 */
const EASING: KeyframeEffectOptions["easing"] = "ease";

/**
 * Options which can be passed to the AccordionItem constructor
 */
type AccordionItemOptions = {
  /**
   * A callback to run when an item is opened,
   * whether due to a click or its `open` methed being called
   */
  onOpen?: (item: AccordionItem) => void;
};

/**
 * A single expandable item in the accordion
 */
class AccordionItem {
  /**
   * Accordion item details element
   */
  el: HTMLDetailsElement;

  /**
   * Summary element (headline)
   */
  summary: HTMLElement;

  /**
   * Element containing everything which will be expanded
   */
  content: HTMLElement;

  /**
   * The running WAAPI animation for the opacity change
   */
  opacityAnimation: null | Animation;

  /**
   * The running WAAPI animation for the height change
   */
  heightAnimation: null | Animation;

  /**
   * Current animation state
   */
  state: null | "closing" | "opening";

  /**
   * Options
   */
  options: AccordionItemOptions;

  /**
   * Timer for closing the item after mouse leave
   */
  mouseLeaveCloseTimer: ReturnType<typeof setTimeout> | null;

  /**
   * Instantiate an item
   */
  constructor(el: HTMLDetailsElement, options: AccordionItemOptions = {}) {
    this.el = el;
    this.options = options;

    {
      const summary = el.querySelector("summary");
      if (summary == null) throw new Error("No summary element");
      this.summary = summary;
    }

    {
      const content = el.querySelector(
        ".accordion__item__content"
      ) as HTMLElement | null;
      if (content == null) throw new Error("No content element");
      this.content = content;
    }

    this.opacityAnimation = null;
    this.heightAnimation = null;
    this.state = null;
    this.mouseLeaveCloseTimer = null;

    // On click, toggle
    this.summary.addEventListener("click", (e) => this.handleClick(e));
  }

  /**
   * Decide what to do on a click event
   */
  handleClick(e: Event) {
    e.preventDefault();
    if (!this.el.open || this.state === "closing") this.open();
    else this.close();
  }

  /**
   * Open the item
   *
   * This method causes the `onOpen` callback to run, if set in options.
   */
  open() {
    // Do nothing if already open or opening
    if ((this.el.open && this.state !== "closing") || this.state === "opening")
      return;

    this.state = "opening";

    // Get the current opacity
    const startOpacity = this.getOpacity();

    // Cancel any already-running opacity animation
    if (this.opacityAnimation) this.opacityAnimation.cancel();

    // Start new opacity animation
    this.opacityAnimation = this.content.animate(
      {
        opacity: [startOpacity, "1"],
      },
      {
        duration: DURATION,
        easing: EASING,
      }
    );

    this.opacityAnimation.addEventListener("finish", () => {
      this.handleAnimationFinish();
    });
    this.opacityAnimation.addEventListener("cancel", () =>
      this.handleAnimationCancel()
    );

    // Get the current height
    const startHeight = this.el.offsetHeight;

    // We need it to be open to calculate the full height
    this.el.open = true;

    // We can't get the full height until layout has occurred
    window.requestAnimationFrame(() => {
      this.el.style.overflow = "hidden";
      this.el.style.height = `${this.el.offsetHeight}px`;

      // Cancel any already-running height animation
      if (this.heightAnimation) this.heightAnimation.cancel();

      // Start new height animation
      this.heightAnimation = this.el.animate(
        {
          height: [
            `${startHeight}px`,
            `${this.summary.offsetHeight + this.content.offsetHeight}px`,
          ],
        },
        {
          duration: DURATION,
          easing: EASING,
        }
      );
    });

    if (this.options.onOpen) this.options.onOpen(this);
  }

  /**
   * Close the item
   */
  close() {
    // Do nothing if already closed or closing
    if (!this.el.open || this.state === "closing") return;

    this.state = "closing";

    // Get the current opacity
    const startOpacity = this.getOpacity();

    // Add a class to hint to CSS that the item is closing.
    // We need this because we want to animate the widget immediately,
    // but we can't remove the "open" attribute until it's fully closed,
    // or the contents are hidden by the browser.
    this.el.classList.add("is-closing");

    this.opacityAnimation = this.content.animate(
      {
        opacity: [startOpacity, "0"],
      },
      {
        duration: DURATION,
        easing: EASING,
      }
    );

    this.opacityAnimation.addEventListener("finish", () => {
      this.el.open = false;
      this.handleAnimationFinish();
    });
    this.opacityAnimation.addEventListener("cancel", () =>
      this.handleAnimationCancel()
    );

    this.el.style.overflow = "hidden";

    // Get the current height;
    // this will otherwise be lost when we cancel the animation
    const startHeight = this.el.offsetHeight;

    // Cancel any already-running animation
    if (this.heightAnimation) this.heightAnimation.cancel();

    // Start new animation
    this.heightAnimation = this.el.animate(
      {
        height: [`${startHeight}px`, `${this.summary.offsetHeight}px`],
      },
      {
        duration: DURATION,
        easing: EASING,
      }
    );
  }

  /**
   * Tidy up after an animation finishes
   */
  handleAnimationFinish() {
    this.handleAnimationCancel();
    this.el.style.height = "";
    this.el.style.overflow = "";
  }

  /**
   * Tidy up after an animation is cancelled
   */
  handleAnimationCancel() {
    this.opacityAnimation = null;
    if (this.heightAnimation) this.heightAnimation.cancel();
    this.heightAnimation = null;
    this.state = null;
    this.el.classList.remove("is-closing");
  }

  /**
   * Get the current opacity of the content
   */
  getOpacity() {
    return window.getComputedStyle(this.content).opacity;
  }
}

/**
 * An accordion
 */
class Accordion {
  /**
   * Items in the accordion
   */
  items: AccordionItem[];

  /**
   * Instantiate the accordion and its items
   */
  constructor(el: HTMLElement) {
    // Instantiate items
    this.items = [...el.querySelectorAll("details")].map(
      (el) => new AccordionItem(el)
    );
  }
}

for (const el of document.querySelectorAll(
  ".accordion"
) as NodeListOf<HTMLElement>) {
  new Accordion(el);
}
